import useIncomingPostMessages from '@rio-cloud/rio-uikit/useIncomingPostMessages';
import usePostMessageSender from '@rio-cloud/rio-uikit/usePostMessageSender';
import type { MouseEvent } from 'react';
import { useCallback, useEffect, useRef } from 'react';

export const MENU_LINK_CLICKED = 'MENU_LINK_CLICKED' as const;
export const MENU_LINK_HANDLED = 'MENU_LINK_HANDLED' as const;

type MenuLinkClickedMessage = {
    type: typeof MENU_LINK_CLICKED;
    href: string;
    requestId: string;
    source: 'admin-menu';
};

type MenuLinkHandledMessage = {
    type: typeof MENU_LINK_HANDLED;
    requestId: string;
};

const isMenuLinkHandledMessage = (data: unknown): data is MenuLinkHandledMessage =>
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    (data as { type?: unknown }).type === MENU_LINK_HANDLED &&
    'requestId' in data &&
    typeof (data as { requestId?: unknown }).requestId === 'string';

const createRequestId = () => {
    if (typeof globalThis.crypto?.randomUUID === 'function') {
        return globalThis.crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const navigateTop = (href: string) => {
    try {
        if (window.top) {
            window.top.location.href = href;
            return;
        }
    } catch {
        window.location.href = href;
    }
};

export const buildAdminMenuSubmenuId = (label: string) =>
    `admin-menu-submenu-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

export const useAdminMenuLinkHandler = () => {
    const { sendMessageToTop } = usePostMessageSender();
    const pendingNavigation = useRef(new Map<string, { timerId: number; href: string }>());

    useIncomingPostMessages((event: MessageEvent) => {
        if (!isMenuLinkHandledMessage(event.data)) {
            return;
        }

        const pending = pendingNavigation.current.get(event.data.requestId);
        if (!pending) {
            return;
        }

        window.clearTimeout(pending.timerId);
        pendingNavigation.current.delete(event.data.requestId);
        navigateTop(pending.href);
    });

    useEffect(
        () => () => {
            pendingNavigation.current.forEach(({ timerId }) => {
                window.clearTimeout(timerId);
            });
            pendingNavigation.current.clear();
        },
        []
    );

    return useCallback(
        (href: string) => (event: MouseEvent<HTMLAnchorElement>) => {
            if (window.top && window.top !== window.self) {
                event.preventDefault();
                const requestId = createRequestId();
                const message: MenuLinkClickedMessage = {
                    type: MENU_LINK_CLICKED,
                    href,
                    requestId,
                    source: 'admin-menu',
                };

                sendMessageToTop(message);
                const fallbackTimer = window.setTimeout(() => {
                    const pending = pendingNavigation.current.get(requestId);
                    if (!pending) {
                        return;
                    }

                    pendingNavigation.current.delete(requestId);
                    navigateTop(pending.href);
                }, 200);

                pendingNavigation.current.set(requestId, { timerId: fallbackTimer, href });
            }
        },
        [sendMessageToTop]
    );
};
